import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  from,
  Operation,
  NextLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { ENVIRONMENT } from '@updater-core/lib/environment';
import { session } from '@updater-core/interactors/shell';
import { startSpan, tagError } from '@updater-core/lib/tracing';
import { track, ERROR, MEASURE } from '@updater-core/lib/tracker';
import { createLoggerFunc } from '@updater-core/lib/logger';

const createLogger = createLoggerFunc(
  ENVIRONMENT.debug.graphQl,
  '[APOLLO_GRAPHQL]',
  'color: #f86761'
);

const { log } = createLogger();

const uri = `${ENVIRONMENT.endpoints.api.url}/graphql`;

const httpLink = new HttpLink({ uri });

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...session.selectors.requestHeaders(session.getState()),
    },
  }));

  return forward(operation);
});

const timeStartLink = new ApolloLink((operation, forward) => {
  const span = startSpan(`fetchGraphQLQuery`);
  span.setTag('operationName', operation.operationName);
  span.setTag('url', uri);

  operation.setContext({
    start: performance.now(),
    span,
  });

  return forward(operation);
});

const trackMeasureLink = new ApolloLink((operation, forward) =>
  forward(operation).map((data) => {
    const { start, span } = operation.getContext();
    const duration = performance.now() - start;
    track({
      object: 'graphql_request',
      verb: MEASURE,
      details: {
        duration,
        operationName: operation.operationName,
      },
    });
    createLogger(` - ${operation.operationName}`).log({
      result: data,
      variables: operation.variables,
    });
    span.finish();
    return data;
  })
);

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  const { span } = operation.getContext();
  span.setTag('error', true);
  if (graphQLErrors) {
    graphQLErrors.forEach((error, i) => {
      const { message, locations, stack, path, ...rest } = error;
      log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
      tagError(span, error);
      span.setTag(`error.message.${i}`, message);
      track({
        object: 'graphql_query',
        verb: ERROR,
        details: {
          operationName: operation.operationName,
          message,
          stack,
          locations,
          ...rest,
        },
      });
    });
  }
  if (networkError) {
    log(`[Network error]: ${networkError}`);
    tagError(span, networkError);
    track({
      object: 'graphql_query',
      verb: ERROR,
      details: {
        message: networkError.message,
        stack: networkError.stack,
        operationName: operation.operationName,
      },
    });
  }
  span.finish();
});

type OperationTypeNode = 'query' | 'mutation' | 'subscription';

const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
const removeTypename = (variables) =>
  JSON.parse(JSON.stringify(variables), omitTypename);

const removeTypenameFromMutationLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    const definition = operation?.query?.definitions.filter(
      (def) => def.kind === 'OperationDefinition'
    )?.[0];
    const mutation: OperationTypeNode = 'mutation';
    if (
      definition?.kind === 'OperationDefinition' &&
      definition?.operation === mutation
    ) {
      // eslint-disable-next-line no-param-reassign
      operation.variables = removeTypename(operation.variables);
    }
    return forward(operation);
  }
);

const link = from([
  timeStartLink,
  removeTypenameFromMutationLink,
  trackMeasureLink,
  errorLink,
  authMiddleware,
  httpLink,
]);

export const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  connectToDevTools: ENVIRONMENT.name !== 'production',
});
